import { EMAIL_VIEW_MODE, GMAIL_FOLDERS, GOOGLE_PROVIDER, OUTLOOK_FOLDERS, OUTLOOK_PROVIDER } from "@/common/const";
import emailInboxService from "@/services/emailInboxService";
import { subscribe, init as initEmailGroupsState } from "@/composables/mail/emailGroupsStateEvents";
import { unsubscribeCollection } from "@/composables/events/handler";

const state = {
	provider: null,
	folderList: null,
	emailFolder: null,
	emailGroupState: null,
	emailViewMode: EMAIL_VIEW_MODE.LIST,
	error: null,
	fullBodyEmails: [],
	groupNewEmails: false,
	// Reference count of in-flight requests, not a plain boolean, so overlapping
	// fetches (e.g. refreshing while switching pages fast) don't prematurely clear
	// the loading state and flash the "no data" empty state.
	loading: 0,
	selectedThreadIds: [],
	threadFullBodyMessages: [],
	threadsOnPage: [],
	threadsPageLimit: 20,
	threadsResultSizeEstimate: 0,
	// Map to store pagination tokens: pageNum -> nextPageToken
	// Example: { "1": null, "2": "token_for_page_2", "3": "token_for_page_3" }
	threadsPageTokens: {},
	currentPageNum: 1,
	threadsEndReached: false
};

const getters = {
	getProvider: state => state.provider,
	getEmailFolder: state => state.emailFolder,
	getEmailGroupState: state => state.emailGroupState,
	getEmailViewMode: state => state.emailViewMode,
	getGroupNewEmails: state => state.groupNewEmails,
	getLoading: state => state.loading > 0,
	getSelectedThreadIds: state => state.selectedThreadIds,
	getThreadFullBodyMessages: state => state.threadFullBodyMessages,
	getThreadsOnPage: state => state.threadsOnPage,
	getThreadsPageLimit: state => state.threadsPageLimit,
	getThreadsResultSizeEstimate: state => state.threadsResultSizeEstimate,
	getError: state => state.error,
	getPageTokens: state => state.threadsPageTokens,
	getCurrentPageNum: state => state.currentPageNum,
	getThreadsEndReached: state => state.threadsEndReached,

	isThreadSelected: state => (threadId) => {
		if (!threadId || !Array.isArray(state.selectedThreadIds)) return false;
		return state.selectedThreadIds.includes(threadId);
	},

	getOpenThreadId: state => state.threadFullBodyMessages[0]?.threadId
};

const mutations = {
	setProvider(state, provider) {
		state.provider = provider;
	},

	setFolderList(state, folderList) {
		state.folderList = folderList;
	},

	setEmailFolder(state, folder) {
		state.emailFolder = folder;
	},

	addFullEmail(state, fullBodyEmail) {
		if (!state.fullBodyEmails.find(oneEmail => fullBodyEmail.emailId === fullBodyEmail.emailId)) {
			state.fullBodyEmails.push(fullBodyEmail);
		}
	},

	clearError(state) {
		state.error = null;
	},

	setEmailGroupState(state, value) {
		state.emailGroupState = value;
	},

	setEmailViewMode(state, value) {
		state.emailViewMode = value;
	},

	setError(state, error) {
		state.error = error;
	},

	setFullEmails(state, fullBodyEmails) {
		state.fullBodyEmails = fullBodyEmails ?? [];
	},

	setGroupNewEmails(state, value) {
		const groupState = state.emailGroupState;

		if (!groupState) {
			state.groupNewEmails = false;
			return;
		}

		const toDate = (ts) => {
			if (!ts || typeof ts.seconds !== "number") return null;
			return new Date(ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1e6));
		};

		const lastMessage = toDate(groupState.lastMessageDate);
		const lastSeen = toDate(groupState.lastSeenDate);

		const hasUnread = groupState.unreadCount > 0;
		const isNewer = lastMessage && lastSeen && lastMessage > lastSeen;

		state.groupNewEmails = hasUnread && isNewer;
	},

	setLoading(state, value) {
		// Increment/decrement instead of overwriting so the flag reflects whether ANY
		// request is still pending. Clamp at 0 to stay resilient to unpaired calls.
		const delta = value ? 1 : -1;

		state.loading = Math.max(0, state.loading + delta);
	},

	setPageToken(state, { pageNum, token }) {
		// Object spread triggers Vue 2 reactivity (direct key assignment does not)
		state.threadsPageTokens = { ...state.threadsPageTokens, [pageNum]: token };
	},

	setCurrentPageNum(state, pageNum) {
		state.currentPageNum = pageNum;
	},

	resetPageTokens(state) {
		state.threadsPageTokens = {};
		state.currentPageNum = 1;
	},

	selectThreadId(state, threadId) {
		if (!threadId) return;
		if (!Array.isArray(state.selectedThreadIds)) {
			state.selectedThreadIds = [];
		}
		if (!state.selectedThreadIds.includes(threadId)) {
			state.selectedThreadIds.push(threadId);
		}
	},

	unselectThreadId(state, threadId) {
		if (!threadId || !Array.isArray(state.selectedThreadIds)) return;
		const index = state.selectedThreadIds.indexOf(threadId);

		if (index !== -1) {
			state.selectedThreadIds.splice(index, 1);
		}
	},

	resetSelectedThreadIds() {
		state.selectedThreadIds = [];
	},

	selectAllThreadsOnPage(state) {
		if (!Array.isArray(state.threadsOnPage)) return;
		state.selectedThreadIds = state.threadsOnPage
			.map(thread => thread.threadId)
			.filter(id => !!id);
	},

	setThreadFullBodyMessages(state, threadFullBodyMessages) {
		state.threadFullBodyMessages = threadFullBodyMessages ?? [];
	},

	setThreadsOnPage(state, threads) {
		state.threadsOnPage = threads ?? [];
	},

	setThreadsPageLimit(state, limit) {
		state.threadsPageLimit = limit;
	},

	setThreadsResultSizeEstimate(state, resultSizeEstimate) {
		state.threadsResultSizeEstimate = resultSizeEstimate ?? 0;
	},

	setThreadsEndReached(state, endReached) {
		state.threadsEndReached = endReached;
	},

	resetThreadsEndReached(state) {
		state.threadsEndReached = false;
	}
};

const actions = {
	initModule({ commit }, { provider }) {
		switch (provider) {
			case GOOGLE_PROVIDER:
				commit("setProvider", GOOGLE_PROVIDER);
				commit("setFolderList", GMAIL_FOLDERS);
				commit("setEmailFolder", GMAIL_FOLDERS.INBOX);
				break;

			case OUTLOOK_PROVIDER:
				commit("setProvider", OUTLOOK_PROVIDER);
				commit("setFolderList", OUTLOOK_FOLDERS);
				commit("setEmailFolder", OUTLOOK_FOLDERS.INBOX);
				break;

			case undefined:
			case "unknown":
				commit("setProvider", null);
				commit("setFolderList", null);
				commit("setEmailFolder", null);
				break;

			default:
				console.warn(`[initModule] Unknown provider: ${provider}`);
				commit("setProvider", null);
				commit("setFolderList", null);
				commit("setEmailFolder", null);
		}
	},

	/**
		* Delete multiple threads from trash
		* @param {string[]} threadIds
		* @param {string} inboxEmail
		*/
	async deleteThreadsFromTrash({ commit, state }, { threadIds, inboxEmail }) {
		commit("clearError");
		commit("setLoading", true);
		try {
			const { successIds } = await emailInboxService.deleteThreadsFromTrash(threadIds, { inboxEmail }, state.provider);

			// Remove successfully moved threads from the lists
			state.threadsOnPage = state.threadsOnPage.filter(
				thread => !successIds.includes(thread.threadId)
			);

			// Also remove them from selected threads, if any remain
			state.selectedThreadIds = state.selectedThreadIds.filter(
				id => !successIds.includes(id)
			);

			return successIds;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async downloadAttachment({ state }, { emailId, attachmentId, inboxEmail }) {
		try {
			const blob = await emailInboxService.downloadAttachment(emailId, attachmentId, { inboxEmail }, state.provider);

			return blob;
		} catch (err) {
			throw err;
		}
	},

	async fetchEmailById({ commit, state }, { emailId, inboxEmail }) {
		commit("setLoading", true);
		commit("clearError");

		try {
			const fullEmail = await emailInboxService.getEmailById(emailId, { inboxEmail }, state.provider);

			commit("addFullEmail", { emailId, ...fullEmail });
			return fullEmail;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async fetchThreadFullBodyMessages({ commit, state }, { threadId, inboxEmail }) {
		if (!state.provider) return;
		commit("setLoading", true);
		commit("clearError");

		try {
			const messages = await emailInboxService.getFullThreadById(
				threadId,
				{ inboxEmail },
				state.provider
			);

			commit("setThreadFullBodyMessages", messages ?? []);

			const markAsRead = (threadsArray) => {
				const thread = threadsArray.find(t => t.threadId === threadId);

				if (thread?.messages?.length) {
					thread.messages[0].unread = false;
				}
			};

			markAsRead(state.threadsOnPage);

			return messages;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async fetchThreads({ state, commit }, { inboxEmail, query, pageNum, folder = null, force = false }) {
		if (!state.provider) return;

		if (force) {
			commit("resetPageTokens");
			commit("setThreadsOnPage", []);
			commit("setThreadsResultSizeEstimate", 0);
			commit("resetThreadsEndReached");
		}

		commit("setLoading", true);
		commit("clearError");

		try {
			// Determine which token to use
			let pageToken = null;

			if (pageNum === 1) {
				// First page always has no token (or null)
				pageToken = state.threadsPageTokens["1"] ?? null;
			} else {
				// For other pages, use the token from the previous page
				pageToken = state.threadsPageTokens[pageNum] ?? null;
			}

			// Build query with the appropriate page token
			const newQuery =
				state.provider === GOOGLE_PROVIDER
					? { ...query, pageToken }
					: { ...query, folder, pageToken };

			const response = await emailInboxService.listThreads(
				newQuery,
				{ inboxEmail },
				state.provider
			);

			const threads = response?.threads ?? [];

			// Save the next page token for the next page
			if (response?.nextPageToken) {
				commit("setPageToken", { pageNum: pageNum + 1, token: response.nextPageToken });
			}

			// Update current page
			commit("setCurrentPageNum", pageNum);
			commit("setThreadsOnPage", threads);

			// Handle result size estimate
			if (!state.threadsEndReached) {
				const hasNext = !!response?.nextPageToken;
				const pageLimit = state.threadsPageLimit;
				const tokensCount = Object.keys(state.threadsPageTokens).length;

				if (hasNext) {
					// Still loading → estimate

					const estimatedByPages = tokensCount * pageLimit;

					if (
						state.threadsResultSizeEstimate > 0 &&
						state.threadsResultSizeEstimate <= estimatedByPages
					) {
						commit("setThreadsResultSizeEstimate", (tokensCount + 1) * pageLimit);
					} else if (
						response?.resultSizeEstimate &&
						state.threadsResultSizeEstimate < response.resultSizeEstimate
					) {
						commit("setThreadsResultSizeEstimate", response.resultSizeEstimate);
					}
				} else {
					// End reached
					commit("setThreadsEndReached", true);

					const isLastPage = threads.length < pageLimit;
					const total = isLastPage
						? (pageNum - 1) * pageLimit + threads.length
						: pageNum * pageLimit;

					commit("setThreadsResultSizeEstimate", total);
				}
			}

			return {
				threads,
				nextPageToken: response?.nextPageToken ?? null
			};

		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async markEmailAsRead({ commit, state }, { emailId, inboxEmail }) {
		commit("clearError");
		try {
			await emailInboxService.markEmailAsRead(emailId, { inboxEmail }, state.provider);
		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	},

	async moveEmailToTrash({ commit, state }, { emailId, inboxEmail }) {
		commit("clearError");

		try {
			await emailInboxService.moveEmailToTrash(emailId, { inboxEmail }, state.provider);

			state.threadsOnPage = state.threadsOnPage.filter(
				thread => !thread.messages?.some(msg => msg.id === emailId)
			);

			return true;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	},

	async moveThreadToTrash({ commit, state }, { threadId, inboxEmail }) {
		commit("clearError");

		try {
			const thread = state.threadsOnPage.find(one => one.threadId === threadId);

			if (!thread?.messages?.length) {
				console.warn(`[moveThreadToTrash] Thread not found or empty: ${threadId}`);
				return;
			}

			const lastMessageId = thread.messages[0]?.id;

			if (!lastMessageId) {
				console.warn(`[moveThreadToTrash] No message ID in thread: ${threadId}`);
				return;
			}

			await emailInboxService.moveEmailToTrash(lastMessageId, { inboxEmail }, state.provider);

			state.threadsOnPage = state.threadsOnPage.filter(
				thread => thread.threadId !== threadId
			);

			return true;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	},

	/**
		* Move multiple threads to trash
		* @param {string[]} threadIds
		* @param {string} inboxEmail
		*/
	async moveThreadsToTrash({ commit, state }, { threadIds, inboxEmail }) {
		commit("clearError");
		commit("setLoading", true);
		try {
			const { successIds } = await emailInboxService.moveThreadsToTrash(threadIds, { inboxEmail }, state.provider);

			// Remove successfully moved threads from the lists
			state.threadsOnPage = state.threadsOnPage.filter(
				thread => !successIds.includes(thread.threadId)
			);

			// Also remove them from selected threads, if any remain
			state.selectedThreadIds = state.selectedThreadIds.filter(
				id => !successIds.includes(id)
			);

			return successIds;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	/**
		* Restore multiple Gmail threads from Trash back to Inbox
		* @param {string[]} threadIds
		* @param {string} inboxEmail
		*/
	async restoreThreadsFromTrash({ commit, state }, { threadIds, inboxEmail }) {
		commit("clearError");
		commit("setLoading", true);

		try {
			const { successIds } = await emailInboxService.restoreThreadsFromTrash(threadIds, { inboxEmail }, state.provider);

			// Remove restored threads from the current view
			state.threadsOnPage = state.threadsOnPage.filter(
				thread => !successIds.includes(thread.threadId)
			);

			// Remove from selected threads, if any
			state.selectedThreadIds = state.selectedThreadIds.filter(
				id => !successIds.includes(id)
			);

			return successIds;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async sendEmailManual({ state, commit, dispatch }, { subject, body, attachments = [], forwardedAttachments = [], inboxEmail, toEmail, replyToMessageId }) {
		commit("clearError");
		const formData = new FormData();

		formData.append("inboxEmail", inboxEmail);
		formData.append("subject", subject);
		formData.append("body", body);
		formData.append("toEmail", toEmail);

		if (replyToMessageId) {
			formData.append("replyToMessageId", replyToMessageId);
			formData.append("threadId", state.threadFullBodyMessages[0]?.threadId);
		}

		attachments.forEach(attachment => {
			formData.append("attachments", attachment);
		});

		formData.append("forwarded", JSON.stringify(forwardedAttachments));

		try {
			const response = await emailInboxService.sendEmailManual(formData, state.provider);

			if (replyToMessageId) {
				const threadId = state.threadFullBodyMessages[0]?.threadId;

				if (threadId) {
					await dispatch("fetchThreadFullBodyMessages", {
						threadId,
						inboxEmail
					});
				}
			}

			return response;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	},

	/**
		* Set a Gmail email as read
		* @param {string} emailId
		* @param {threadId} threadId
		* @param {string} inboxEmail
		*/
	async setEmailAsRead({ commit, state }, { emailId, threadId, inboxEmail }) {
		commit("clearError");

		try {
			await emailInboxService.setEmailAsRead(emailId, { inboxEmail }, state.provider);

			// Set message as read in page list
			const updateThread = (threadsArray) => {
				const thread = threadsArray.find(t => t.threadId === threadId);

				if (!thread?.messages?.length) return;

				const msg = thread.messages.find(m => m.id === emailId);

				if (msg) msg.unread = false;

				// The thread list binds its read/unread (bold) state to the first message,
				// so keep it in sync even when the read view passes a different message id.
				thread.messages[0].unread = false;
			};

			updateThread(state.threadsOnPage);
		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	},

	/**
		* Set a Gmail email as unread
		* @param {string} emailId
		* @param {threadId} threadId
		* @param {string} inboxEmail
		*/
	async setEmailAsUnread({ commit, state }, { emailId, threadId, inboxEmail }) {
		commit("clearError");
		try {
			await emailInboxService.setEmailAsUnread(emailId, { inboxEmail }, state.provider);
			// Set message as unread in page list
			const updateThread = (threadsArray) => {
				const thread = threadsArray.find(t => t.threadId === threadId);

				if (!thread?.messages?.length) return;

				const msg = thread.messages.find(m => m.id === emailId);

				if (msg) msg.unread = true;

				// The thread list binds its read/unread (bold) state to the first message,
				// so keep it in sync even when the read view passes a different message id.
				thread.messages[0].unread = true;
			};

			updateThread(state.threadsOnPage);
		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	},

	/**
		* Toggle Gmail email read/unread status explicitly
		* @param {string} emailId
		* @param {threadId} threadId
		* @param {string} inboxEmail
		* @param {boolean} isRead - true to mark as read, false to mark as unread
		*/
	async toggleEmailReadStatus({ commit, state }, { emailId, threadId, inboxEmail, isRead }) {
		commit("clearError");

		try {
			if (isRead) {
				await emailInboxService.setEmailAsRead(emailId, { inboxEmail }, state.provider);
			} else {
				await emailInboxService.setEmailAsUnread(emailId, { inboxEmail }, state.provider);
			}

			const updateThread = (threadsArray) => {
				const thread = threadsArray.find(t => t.threadId === threadId);

				if (!thread) return;
				const msg = thread.messages?.find(m => m.id === emailId);

				if (msg) msg.unread = !isRead;
			};

			updateThread(state.threadsOnPage);

		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	},

	/**
	* Toggle Gmail email starred/unstarred status
	*/
	async toggleEmailStarred({ commit, state }, { emailId, starred, inboxEmail }) {
		commit("clearError");
		try {
			await emailInboxService.toggleStarredStatus(emailId, starred, { inboxEmail }, GOOGLE_PROVIDER);

			return true;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	},

	/**
		* Toggle Gmail thread starred/unstarred status
		*/
	async toggleThreadStarred({ commit, state }, { threadId, starred, inboxEmail }) {
		commit("clearError");

		const markStarred = (threadsArray) => {
			const thread = threadsArray.find(t => t.threadId === threadId);

			if (thread?.messages?.length) {
				thread.messages[0].starred = true;
			}
		};

		const markUnstarred = (threadsArray, successfulIds) => {
			for (const thread of threadsArray) {
				if (thread.threadId !== threadId) continue;

				if (thread.messages?.length) {
					thread.messages.forEach(msg => {
						if (successfulIds.includes(msg.id)) {
							msg.starred = false;
						}
					});
				}
			}
		};

		try {
			if (starred === true) {
				// Add a star to the first message
				const thread = state.threadsOnPage.find(t => t.threadId === threadId);

				const firstMsg = thread?.messages?.[0];

				if (!firstMsg) throw new Error("Thread not found or has no messages");

				await emailInboxService.toggleStarredStatus(firstMsg.id, true, { inboxEmail }, GOOGLE_PROVIDER);

				markStarred(state.threadsOnPage);
			} else {
				// Remove stars, ignoring errors
				const thread = state.threadsOnPage.find(t => t.threadId === threadId);

				if (!thread?.messages?.length) throw new Error("Thread not found or has no messages");

				const successfulIds = [];

				for (const msg of thread.messages) {
					if (msg.starred === true) {
						try {
							await emailInboxService.toggleStarredStatus(msg.id, false, { inboxEmail }, GOOGLE_PROVIDER);
							successfulIds.push(msg.id);
						} catch (err) {
							console.warn(`Failed to unstar msg.id=${msg.id}:`, err.message);
						}
					}
				}

				markUnstarred(state.threadsOnPage, successfulIds);
			}

			return true;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	},

	/**
		* Toggle Outlook flagged status of an email
		* @param {string} emailId
		* @param {string} threadId
		* @param {boolean} flagged
		* @param {string} inboxEmail
		*/
	async toggleEmailFlagged({ commit, state }, { emailId, threadId, flagged, inboxEmail }) {
		commit("clearError");

		const toggleInThreadList = (threads) => {
			const thread = threads.find(t => t.threadId === threadId);

			if (!thread) return;

			const msg = thread.messages?.find(m => m.id === emailId);

			if (msg) msg.flagged = flagged;
		};

		try {
			await emailInboxService.toggleFlaggedStatus(emailId, flagged, { inboxEmail }, OUTLOOK_PROVIDER);
			toggleInThreadList(state.threadsOnPage);
			return true;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	},

	/**
		* Toggle spam status (Gmail: label SPAM, Outlook: move to JunkEmail or Inbox)
		* Removes thread from lists after moving
		* @param {string} threadId
		* @param {boolean} isSpam
		* @param {string} inboxEmail
		*/
	async toggleThreadSpamStatus({ commit, state }, { threadId, isSpam, inboxEmail }) {
		commit("clearError");

		try {
			await emailInboxService.toggleThreadSpamStatus(threadId, isSpam, { inboxEmail }, state.provider);

			state.threadsOnPage = state.threadsOnPage.filter(t => t.threadId !== threadId);
			state.selectedThreadIds = state.selectedThreadIds.filter(id => id !== threadId);

			return true;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	}

};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,

	init({ watch, login, commit, dispatch }) {
		initEmailGroupsState(commit);

		watch(
			() => login.userGroup?.email,
			(newEmail, oldEmail) => {
				if (newEmail === oldEmail) return;

				if (!newEmail && oldEmail) {
					unsubscribeCollection("email-groups-state");
				}

				if (login.userGroup?.id) {
					subscribe(login.userGroup.id);
				} else {
					unsubscribeCollection("email-groups-state");
				}
			}
		);

		watch(
			() => login.refreshed,
			(refreshed) => {
				if (refreshed && login.userGroup?.email) {
					subscribe(login.userGroup.id);
					// Re-init module with new provider
					dispatch("initModule", { provider: login.userGroup?. emailProvider });
					console.log("[EMAIL-GROUPS-STATE] userGroup email provider updated:", login.userGroup?. emailProvider);
				}
			}
		);

		watch(
			() => login.userGroup?.emailProvider,
			(newValue, oldValue) => {

				// Re-init module with new provider
				dispatch("initModule", { provider: newValue });
				console.log("[EMAIL-GROUPS-STATE] userGroup email provider changed:", oldValue, "->", newValue);
			}
		);
	}
};
